import { useMemo } from 'react'
import { ReadSyncIssuesDocument } from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { toSyncParameters } from 'modules/integrations/lib/parameters'
import {
  SyncIssue,
  useCompensationsSyncIssues,
} from 'modules/integrations/hooks/SyncContext/issues'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

interface UseReadSyncIssuesLazyQueryResult {
  syncResultIssues: SyncIssue[]
  loading: boolean
}

export const useReadSyncIssuesLazyQuery = ({
  skip = false,
}: {
  skip?: boolean
}): UseReadSyncIssuesLazyQueryResult => {
  const {
    syncEnabled,
    marketplaceIntegration,
    contextParameters,
    configuration,
    isBookkeeperView,
    companyId,
  } = usePrivateIntegrationsSyncContext()

  const [fetchReadSyncIssuesQuery, { data, loading }] = useLazyQuery(ReadSyncIssuesDocument)

  const serializedContextParameters = JSON.stringify(contextParameters)

  useUnsafeEffect(() => {
    if (skip || !syncEnabled || !marketplaceIntegration || !companyId) return

    fetchReadSyncIssuesQuery({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        parameters: toSyncParameters(contextParameters),
      },
      context: {
        isBookkeeperView,
      },
    })
  }, [
    skip,
    syncEnabled,
    marketplaceIntegration,
    companyId,
    configuration.origin,
    serializedContextParameters,
    isBookkeeperView,
    fetchReadSyncIssuesQuery,
  ])

  const syncIssues = useMemo(
    () =>
      (data?.integrations?.syncIssuesConnection.nodes ?? []).map((issue) => ({
        affectedNames: issue.affectedNames,
        errorMessage: {
          key: issue.errorMessage.key,
          value: issue.errorMessage.value,
        },
      })),
    [data]
  )

  const isValidation = false

  return {
    syncResultIssues: useCompensationsSyncIssues(syncIssues, marketplaceIntegration, isValidation),
    loading,
  }
}
